/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { forwardRef, useCallback, useRef, useState } from 'react';

import { VisuallyHidden } from '@coursera/cds-core';

import Alert from 'bundles/authentication-common/components/Alert';

import _t from 'i18n!nls/authentication';

type OwnProps = {
  onInvalid?: () => void;
  onSubmit?: (values: Values) => void;
  constraints?: Record<string, Validation>;
  /**
   * When true, suppresses all validation error messages.
   * Useful for conditionally rendered fields or when validation errors
   * are superseded by other flows (e.g., enterprise SSO).
   */
  hideErrors?: boolean;
};

type Props = OwnProps & Omit<React.ComponentPropsWithRef<'form'>, 'css' | keyof OwnProps>;

type Validation = (value: string) => string | false;

type Values = Record<string, string>;

function data(form: HTMLFormElement): Record<string, string> {
  const formElements = Array.from(form.elements);
  const result: Record<string, string> = {};
  (formElements as HTMLInputElement[])
    .filter((element) => element.name && !element.disabled)
    .map((element) => [element.name, element.value])
    .forEach(([name, val]) => {
      // @ts-expect-error TS2538 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      result[name] = val;
    });

  return result;
}

function validate(values: Record<string, string>, constraints: Record<string, Validation>): string[] {
  return (
    Object.entries(constraints)
      .map(([key, cb]) => cb(values[key] || ''))
      // I don't know why typescript doesn't understand that filter (string|false)[] => string[]
      .filter(Boolean) as string[]
  );
}

const styles = {
  error: css`
    list-style: none;
    margin: 0;
    padding: 0;
  `,
};

const Form = forwardRef<HTMLFormElement, Props>(
  ({ children, constraints, onInvalid = () => {}, onSubmit = () => {}, hideErrors = false, ...props }, ref) => {
    const [errors, setErrors] = useState<string[]>([]);
    const [ariaMessages, setAriaMessages] = useState<string[]>([]);
    const timeoutRef = useRef<number>();

    const updateErrors = useCallback<(errorMessages: string[]) => void>(
      (errorMessages: string[]) => {
        setErrors(errorMessages);
        // we clear out the current messages and then enqueue setting new messages so that aria-live will notice the new children and announce them
        setAriaMessages([]);
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = window.setTimeout(() => {
          setAriaMessages(errorMessages);
        }, 1000);
      },
      [setErrors, setAriaMessages, timeoutRef]
    );

    const handleSubmit = useCallback<(event: React.FormEvent<HTMLFormElement>) => void>(
      (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const values = data(event.target as HTMLFormElement);

        const validation = validate(values, constraints || {});
        updateErrors(validation);

        if (validation.length === 0) {
          onSubmit(values);
        } else if (onInvalid) {
          onInvalid();
        }
      },
      [constraints, onInvalid, onSubmit, updateErrors]
    );

    return (
      <React.Fragment>
        <form
          {...props}
          // eslint-disable-next-line no-script-url
          action="javascript:void(0)"
          noValidate
          ref={ref}
          onSubmit={handleSubmit}
        >
          <div>
            {errors.length > 0 && !hideErrors && (
              // Because we need to handle re-announcing error messages on submit, and we don't want to have to visually flash the error list, we handle the aria announcements separately below
              <Alert liveAnnounce={false} data-testid="validationErrors">
                <ul css={styles.error}>
                  {errors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              </Alert>
            )}
          </div>
          {children}
        </form>
        <VisuallyHidden aria-live="assertive">
          {ariaMessages.length > 0 && (
            <div data-testid="ariaValidationErrors">
              <p>{_t('Unsuccessful, the following errors were found: ')}</p>
              <ul>
                {ariaMessages.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            </div>
          )}
        </VisuallyHidden>
      </React.Fragment>
    );
  }
);

export default Form;
